import { useGetRevisionHistory } from '@/features/agents/hooks/use-get-revision-history';
import { useParams } from 'react-router-dom';
import { Feed } from '@/features/agents/components/feed';
import { useRevertRevision } from '@/features/agents/hooks/use-revert-revision';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingScreenSpinner } from '@/components/loading-screen-spinner';

interface RevisionHistoryProps {
  branch: string;
}

export function RevisionHistory({ branch }: RevisionHistoryProps) {
  const { uuid } = useParams();
  const { data: revisionHistory, isLoading: isHistoryLoading } =
    useGetRevisionHistory(uuid!);
  const revertRevisionMutation = useRevertRevision();
  const queryClient = useQueryClient();

  const items = (revisionHistory?.[branch] || [])?.map((revision: any) => ({
    id: revision.short_id,
    revision: revision.short_id,
    comment: revision.title,
    date: revision.created_at,
    changedBy: revision.author_name,
  }));

  function handleRevertClick(revisionId: string) {
    revertRevisionMutation.mutate(
      {
        uuid: uuid!,
        revisionId,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['revision-history', uuid],
          });
          queryClient.invalidateQueries({
            queryKey: ['agent', uuid],
          });
        },
      }
    );
  }

  const isLoading = isHistoryLoading || revertRevisionMutation.isLoading;

  return (
    <div className="relative">
      <Feed
        title="Revision History"
        items={items}
        onRevert={handleRevertClick}
        className="max-h-[312px] h-full overflow-y-auto"
      />
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center">
          <LoadingScreenSpinner />
        </div>
      )}
    </div>
  );
}
